import { ScrollableVirtualList, ScrollableVirtualListHandle } from "client/Components";
import { SelectableItemOptions, useUserTimezone } from "client/Hooks";
import { isNotNil } from "client/Misc/utils";
import { DocumentItemViewModel } from "client/Api/Documents/SharePoint/common";
import {
	forwardRef,
	ReactElement,
	Ref,
	RefObject,
	useImperativeHandle,
	useRef
} from "react";
import { SharePointDocumentAction, SharePointDocumentActionContext } from "../../SharePoint/actions";
import { AdditionalInformation } from "../../SharePoint/SharePointListView";
import { GridItem } from "../../SharePoint/SharePointOverView";
import { ItemPickerListItem } from "../ItemPickerListItem";
import { checkIsDocumentDisabled } from "./SharePointPickerOverview";
import { TimeZone } from "client/DateAndTime/timezones";
import { ItemPickerMode } from "../ItemPicker";
import { DocumentNoResult } from "../../DocumentNoResult";

export type SharePointPickerListViewHandle = {
	resetScrollPosition: () => void;
};

export type SharePointPickerListViewProps = {
	isBusy: boolean;
	hasMoreItems: boolean;
	onLoadMoreItems: () => void;
	items: readonly GridItem[];
	selectionFor: (item: GridItem) => SelectableItemOptions;
	isInSelectMode: boolean;
	onOpenItem: (item: GridItem) => void;
	mode: ItemPickerMode;
	getAvailableOpenActions: (item: DocumentItemViewModel) => readonly SharePointDocumentAction[];
	actionContext: SharePointDocumentActionContext;
	hasSearch: boolean;
	loadMoreIndicatorVersion: number;
};

export const SharePointPickerListView = forwardRef(
	(props: SharePointPickerListViewProps, ref: Ref<SharePointPickerListViewHandle>): ReactElement => {
		const {
			isBusy,
			hasMoreItems,
			onLoadMoreItems,
			items,
			onOpenItem,
			selectionFor,
			isInSelectMode,
			mode,
			getAvailableOpenActions,
			actionContext,
			hasSearch,
			loadMoreIndicatorVersion
		} = props;

		const userTimezone: TimeZone = useUserTimezone();
		const listHandle: RefObject<ScrollableVirtualListHandle<GridItem> | null> = useRef<ScrollableVirtualListHandle<GridItem>>(null);

		useImperativeHandle(
			ref,
			(): SharePointPickerListViewHandle => ({
				resetScrollPosition: (): void => {
					listHandle.current
						?.scrollToTop();
				}
			}),
			[]
		);

		if (!isBusy && items.length === 0) {
			return (
				<DocumentNoResult
					hasSearch={hasSearch}
				/>
			);
		}

		return (
			<ScrollableVirtualList
				items={items}
				estimatedHeightOfAnItem={64}
				itemKey={getUniqueKey}
				ref={listHandle}
				renderAfterItems={
					<ScrollableVirtualList.LoadMoreIndicator
						key={loadMoreIndicatorVersion}
						isLoadingData={isBusy}
						hasMoreItems={hasMoreItems}
						onRequestMoreItems={onLoadMoreItems}
					/>
				}
				trackingId="sharepoint-picker-list-view"
			>
				{(x: GridItem): ReactElement => (
					<ItemPickerListItem
						name={x.name}
						documentKind={x.model.itemType}
						isInSelectMode={isInSelectMode}
						onOpenItem={() => onOpenItem(x)}
						isInfected={x.isInfected ?? false}
						additionalInformation={<AdditionalInformation item={x} timezone={userTimezone} />}
						isBookmarked={isNotNil(x.model.bookmarkId)}
						selectable={selectionFor(x)}
						isDisabled={checkIsDocumentDisabled(x, mode)}
						actionContext={{ ...actionContext, selection: [x.model] }}
						actions={getAvailableOpenActions(x.model)}
					/>
				)}
			</ScrollableVirtualList>
		);
	}
);
SharePointPickerListView.displayName = "SharePointPickerListView";

function getUniqueKey(item: GridItem): string {
	return item.uniqueKey;
}
